import React from 'react';
import { FileSignature } from 'lucide-react';
import { useMapStore } from '../../../store/useMapStore';
import { useTranslation } from '../../../i18n/translations';
import ResultCard from '../../../components/ResultCard';
import type { RegistrationBoundaryProperties, RegistrationOfficeProperties } from '../../../types/gis';

interface RegistrationCardProps {
  onReport?: (data: { label: string; value: string }[]) => void;
  shareUrl?: string;
  shareLabel?: string;
}

export const RegistrationCard: React.FC<RegistrationCardProps> = ({ 
  onReport,
  shareUrl,
  shareLabel
}) => {
  const { 
    selectedRegistrationOffice, 
    setSelectedRegistrationOffice,
    jurisdictionGeometry,
    isResultMinimized,
    setIsResultMinimized 
  } = useMapStore();
  const { t } = useTranslation();

  if (!selectedRegistrationOffice) return null;

  const props = selectedRegistrationOffice.properties as RegistrationBoundaryProperties & RegistrationOfficeProperties;
  
  const data = [
    { label: t('DISTRICT'), value: props.district || props.district_n || '' },
    { label: t('ZONE_NAME'), value: props.zone_name || '' },
    { label: t('TALUK'), value: props.taluk_name || '' },
    { label: t('REVENUE_VILLAGE'), value: props.revenue_vi || '' },
    { label: t('SRO_ID'), value: props.sro_id || '', isPill: true },
  ].filter(d => d.value);

  const title = props.sro_name || props.office_nam || t('SRO');
  const officeLocation = selectedRegistrationOffice.geometry.type === 'Point' 
    ? selectedRegistrationOffice.geometry.coordinates as [number, number]
    : null;

  const googleMapsUrl = officeLocation 
    ? `https://www.google.com/maps/dir/?api=1&destination=${officeLocation[1]},${officeLocation[0]}`
    : undefined;

  return (
    <ResultCard
      title={title}
      icon={<FileSignature size={20} />}
      themeColor="indigo"
      data={data}
      onClose={() => setSelectedRegistrationOffice(null)}
      onAction={googleMapsUrl ? () => window.open(googleMapsUrl, '_blank') : undefined}
      actionLabel={t('DIRECTIONS')}
      onMinimize={() => setIsResultMinimized(!isResultMinimized)}
      featureId={`sro-${props.sro_id || props.office_nam}`}
      shareLabel={shareLabel || t('SRO')}
      geometry={jurisdictionGeometry || selectedRegistrationOffice.geometry}
      pointLocation={officeLocation}
      directionsUrl={googleMapsUrl}
      onReport={onReport}
      shareUrl={shareUrl}
      confidence={props.match_confidence ? {
        level: props.match_confidence as 'high' | 'medium' | 'low',
        message: props.match_confidence === 'high' 
          ? t('CONFIDENCE_HIGH') 
          : props.match_confidence === 'medium' 
            ? t('CONFIDENCE_MEDIUM') 
            : t('CONFIDENCE_LOW')
      } : undefined}
    />
  );
};
